/**
 * Port Data Validation Utilities
 * Validates port records for data accuracy and consistency
 */

// Validation severity levels
export const SEVERITY = {
  ERROR: 'ERROR',
  WARNING: 'WARNING',
  INFO: 'INFO'
};

/**
 * Validate UN/LOCODE format
 * Format: 2 letters (country code) + 3 alphanumeric (location code)
 * Example: NLRTM, SGSIN, CNTXG
 */
export function validateLocode(locode) {
  if (!locode || locode.trim() === '') {
    return {
      valid: false,
      issue: 'MISSING_LOCODE',
      severity: SEVERITY.WARNING,
      message: 'UN/LOCODE is missing'
    };
  }

  const locodePattern = /^[A-Z]{2}[A-Z0-9]{3}$/;
  if (!locodePattern.test(locode)) {
    return {
      valid: false,
      issue: 'INVALID_LOCODE_FORMAT',
      severity: SEVERITY.ERROR,
      message: `UN/LOCODE '${locode}' does not match format: 2 letters + 3 alphanumeric`
    };
  }

  return {
    valid: true,
    issue: null,
    severity: null,
    message: 'Valid UN/LOCODE format'
  };
}

/**
 * Validate latitude range
 * Valid range: -90 to +90
 */
export function validateLatitude(latitude) {
  const lat = parseFloat(latitude);

  if (isNaN(lat)) {
    return {
      valid: false,
      issue: 'INVALID_LATITUDE',
      severity: SEVERITY.ERROR,
      message: 'Latitude is not a valid number'
    };
  }

  if (lat === 0) {
    return {
      valid: false,
      issue: 'ZERO_LATITUDE',
      severity: SEVERITY.WARNING,
      message: 'Latitude is zero (likely invalid)'
    };
  }

  if (lat < -90 || lat > 90) {
    return {
      valid: false,
      issue: 'LATITUDE_OUT_OF_RANGE',
      severity: SEVERITY.ERROR,
      message: `Latitude ${lat} is out of valid range (-90 to +90)`
    };
  }

  return {
    valid: true,
    issue: null,
    severity: null,
    message: 'Valid latitude'
  };
}

/**
 * Validate longitude range
 * Valid range: -180 to +180
 */
export function validateLongitude(longitude) {
  const lon = parseFloat(longitude);

  if (isNaN(lon)) {
    return {
      valid: false,
      issue: 'INVALID_LONGITUDE',
      severity: SEVERITY.ERROR,
      message: 'Longitude is not a valid number'
    };
  }

  if (lon === 0) {
    return {
      valid: false,
      issue: 'ZERO_LONGITUDE',
      severity: SEVERITY.WARNING,
      message: 'Longitude is zero (likely invalid)'
    };
  }

  if (lon < -180 || lon > 180) {
    return {
      valid: false,
      issue: 'LONGITUDE_OUT_OF_RANGE',
      severity: SEVERITY.ERROR,
      message: `Longitude ${lon} is out of valid range (-180 to +180)`
    };
  }

  return {
    valid: true,
    issue: null,
    severity: null,
    message: 'Valid longitude'
  };
}

/**
 * Validate foreign key reference
 */
export function validateForeignKey(value, fieldName, referenceData, referenceName) {
  if (!value || value === '' || value === '0') {
    return {
      valid: false,
      issue: `MISSING_${fieldName.toUpperCase()}`,
      severity: SEVERITY.ERROR,
      message: `${fieldName} is missing or zero`
    };
  }

  const id = String(value);
  const exists = referenceData.some(item => String(item.id) === id);

  if (!exists) {
    return {
      valid: false,
      issue: `INVALID_${fieldName.toUpperCase()}_REFERENCE`,
      severity: SEVERITY.ERROR,
      message: `${fieldName} ${value} does not exist in ${referenceName} table`
    };
  }

  return {
    valid: true,
    issue: null,
    severity: null,
    message: `Valid ${fieldName} reference`
  };
}

/**
 * Point-in-polygon test using ray-casting algorithm
 * @param {Array} point - [longitude, latitude]
 * @param {Array} polygon - Array of [longitude, latitude] coordinates
 */
function pointInPolygon(point, polygon) {
  const x = point[0];
  const y = point[1];
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];

    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    
    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Check if a point is within any polygon of a MultiPolygon or Polygon geometry
 */
function pointInGeometry(point, geometry) {
  if (!geometry || !geometry.type || !geometry.coordinates) {
    return false;
  }

  if (geometry.type === 'Polygon') {
    // For Polygon: coordinates[0] is outer ring
    return pointInPolygon(point, geometry.coordinates[0]);
  } else if (geometry.type === 'MultiPolygon') {
    // For MultiPolygon: check each polygon
    for (const polygon of geometry.coordinates) {
      if (pointInPolygon(point, polygon[0])) {
        return true;
      }
    }
    return false;
  }

  return false;
}

/**
 * Validate timezone against geographic location
 * @param {number} latitude - Port latitude
 * @param {number} longitude - Port longitude
 * @param {string} timezoneId - Assigned timezone ID
 * @param {Array} timezonesData - Timezone data with IANA names
 * @param {Object} timezonesGeoJSON - GeoJSON FeatureCollection of timezone boundaries
 */
export function validateTimezoneGeography(latitude, longitude, timezoneId, timezonesData, timezonesGeoJSON) {
  const lat = parseFloat(latitude);
  const lon = parseFloat(longitude);

  if (isNaN(lat) || isNaN(lon)) {
    return {
      valid: false,
      issue: 'INVALID_COORDINATES',
      severity: SEVERITY.ERROR,
      message: 'Cannot validate timezone: invalid coordinates'
    };
  }

  // Find timezone record
  const timezone = timezonesData.find(tz => String(tz.id) === String(timezoneId));
  if (!timezone || !timezone.value) {
    return {
      valid: false,
      issue: 'TIMEZONE_NOT_FOUND',
      severity: SEVERITY.ERROR,
      message: `Timezone ID ${timezoneId} not found in timezone data`
    };
  }

  const expectedTzName = timezone.value; // IANA name like "Europe/London"

  // If no GeoJSON data available, skip geographic validation
  if (!timezonesGeoJSON || !timezonesGeoJSON.features) {
    return {
      valid: true,
      issue: 'TIMEZONE_GEOJSON_UNAVAILABLE',
      severity: SEVERITY.INFO,
      message: 'Geographic timezone validation skipped (GeoJSON not loaded)'
    };
  }

  const point = [lon, lat]; // GeoJSON uses [longitude, latitude] order

  // Find all timezone features that contain this point
  const matchingTimezones = timezonesGeoJSON.features.filter(feature => {
    return pointInGeometry(point, feature.geometry);
  });

  if (matchingTimezones.length === 0) {
    return {
      valid: false,
      issue: 'POINT_NOT_IN_ANY_TIMEZONE',
      severity: SEVERITY.WARNING,
      message: `Coordinates (${lat}, ${lon}) do not fall within any timezone boundary (may be in ocean/disputed area)`
    };
  }

  // Check if the assigned timezone matches any of the geographic timezones
  const tzidMatches = matchingTimezones.some(feature => {
    const featureTzid = feature.properties?.tzid;
    // Compare IANA timezone names (convert underscores in filename to slashes)
    return featureTzid === expectedTzName;
  });

  if (!tzidMatches) {
    const actualTzids = matchingTimezones.map(f => f.properties?.tzid).filter(Boolean);
    return {
      valid: false,
      issue: 'TIMEZONE_MISMATCH',
      severity: SEVERITY.WARNING,
      message: `Port timezone '${expectedTzName}' does not match geographic location. Expected one of: ${actualTzids.join(', ')}`
    };
  }

  return {
    valid: true,
    issue: null,
    severity: null,
    message: 'Timezone matches geographic location'
  };
}

/**
 * Find duplicate ports
 * @param {Object} port - Port to check
 * @param {Array} allPorts - All ports to compare against
 * @param {number} coordinateThreshold - Distance threshold in degrees (default 0.01 ≈ 1km)
 */
export function findDuplicates(port, allPorts, coordinateThreshold = 0.01) {
  const duplicates = {
    byName: [],
    byCoordinates: []
  };

  const portLat = parseFloat(port.latitude);
  const portLon = parseFloat(port.longitude);
  const portId = String(port.id);

  for (const otherPort of allPorts) {
    const otherId = String(otherPort.id);
    
    // Skip same port
    if (otherId === portId) continue;

    // Check name duplicates (case-insensitive)
    if (port.name && otherPort.name &&
        port.name.toLowerCase().trim() === otherPort.name.toLowerCase().trim()) {
      duplicates.byName.push({
        id: otherPort.id,
        name: otherPort.name,
        locode: otherPort.un_locode
      });
    }

    // Check coordinate duplicates
    if (!isNaN(portLat) && !isNaN(portLon)) {
      const otherLat = parseFloat(otherPort.latitude);
      const otherLon = parseFloat(otherPort.longitude);

      if (!isNaN(otherLat) && !isNaN(otherLon)) {
        const latDiff = Math.abs(portLat - otherLat);
        const lonDiff = Math.abs(portLon - otherLon);

        if (latDiff < coordinateThreshold && lonDiff < coordinateThreshold) {
          duplicates.byCoordinates.push({
            id: otherPort.id,
            name: otherPort.name,
            locode: otherPort.un_locode,
            distance: Math.sqrt(latDiff * latDiff + lonDiff * lonDiff)
          });
        }
      }
    }
  }

  return duplicates;
}

/**
 * Validate a complete port record
 * @param {Object} port - Port record to validate
 * @param {Object} referenceData - Object containing countries, timezones, allPorts arrays
 * @param {Object} timezonesGeoJSON - Optional GeoJSON for timezone geography validation
 */
export function validatePort(port, referenceData, timezonesGeoJSON = null) {
  const { countries, timezones, allPorts } = referenceData;
  
  const validationResults = {
    port_id: port.id,
    port_name: port.name,
    un_locode: port.un_locode,
    errors: [],
    warnings: [],
    info: [],
    isValid: true
  };

  // Validate LOCODE
  const locodeResult = validateLocode(port.un_locode);
  if (!locodeResult.valid) {
    const entry = {
      field: 'un_locode',
      issue: locodeResult.issue,
      message: locodeResult.message
    };
    
    if (locodeResult.severity === SEVERITY.ERROR) {
      validationResults.errors.push(entry);
      validationResults.isValid = false;
    } else if (locodeResult.severity === SEVERITY.WARNING) {
      validationResults.warnings.push(entry);
    }
  }

  // Validate Latitude
  const latResult = validateLatitude(port.latitude);
  if (!latResult.valid) {
    const entry = {
      field: 'latitude',
      issue: latResult.issue,
      message: latResult.message,
      value: port.latitude
    };
    
    if (latResult.severity === SEVERITY.ERROR) {
      validationResults.errors.push(entry);
      validationResults.isValid = false;
    } else if (latResult.severity === SEVERITY.WARNING) {
      validationResults.warnings.push(entry);
    }
  }

  // Validate Longitude
  const lonResult = validateLongitude(port.longitude);
  if (!lonResult.valid) {
    const entry = {
      field: 'longitude',
      issue: lonResult.issue,
      message: lonResult.message,
      value: port.longitude
    };
    
    if (lonResult.severity === SEVERITY.ERROR) {
      validationResults.errors.push(entry);
      validationResults.isValid = false;
    } else if (lonResult.severity === SEVERITY.WARNING) {
      validationResults.warnings.push(entry);
    }
  }

  // Validate Country FK
  if (countries && countries.length > 0) {
    const countryResult = validateForeignKey(
      port.country_id,
      'country_id',
      countries,
      'countries'
    );
    if (!countryResult.valid) {
      validationResults.errors.push({
        field: 'country_id',
        issue: countryResult.issue,
        message: countryResult.message,
        value: port.country_id
      });
      validationResults.isValid = false;
    }
  }

  // Validate Timezone FK
  if (timezones && timezones.length > 0) {
    const timezoneResult = validateForeignKey(
      port.time_zone_id,
      'time_zone_id',
      timezones,
      'timezones'
    );
    if (!timezoneResult.valid) {
      validationResults.errors.push({
        field: 'time_zone_id',
        issue: timezoneResult.issue,
        message: timezoneResult.message,
        value: port.time_zone_id
      });
      validationResults.isValid = false;
    } else if (timezonesGeoJSON) {
      // Validate timezone geography if GeoJSON is available
      const tzGeoResult = validateTimezoneGeography(
        port.latitude,
        port.longitude,
        port.time_zone_id,
        timezones,
        timezonesGeoJSON
      );
      
      if (!tzGeoResult.valid) {
        const entry = {
          field: 'timezone_geography',
          issue: tzGeoResult.issue,
          message: tzGeoResult.message
        };
        
        if (tzGeoResult.severity === SEVERITY.ERROR) {
          validationResults.errors.push(entry);
          validationResults.isValid = false;
        } else if (tzGeoResult.severity === SEVERITY.WARNING) {
          validationResults.warnings.push(entry);
        } else if (tzGeoResult.severity === SEVERITY.INFO) {
          validationResults.info.push(entry);
        }
      }
    }
  }

  // Check for duplicates
  if (allPorts && allPorts.length > 0) {
    const duplicates = findDuplicates(port, allPorts);
    
    if (duplicates.byName.length > 0) {
      validationResults.warnings.push({
        field: 'name',
        issue: 'DUPLICATE_NAME',
        message: `Port name matches ${duplicates.byName.length} other port(s)`,
        duplicates: duplicates.byName
      });
    }
    
    if (duplicates.byCoordinates.length > 0) {
      validationResults.warnings.push({
        field: 'coordinates',
        issue: 'DUPLICATE_COORDINATES',
        message: `Coordinates are very close to ${duplicates.byCoordinates.length} other port(s)`,
        duplicates: duplicates.byCoordinates
      });
    }
  }

  return validationResults;
}

/**
 * Generate validation summary statistics
 */
export function generateValidationSummary(validationResults) {
  const summary = {
    total: validationResults.length,
    valid: 0,
    withErrors: 0,
    withWarnings: 0,
    errorsByType: {},
    warningsByType: {},
    portsMissingLocode: [],
    portsWithInvalidCoordinates: [],
    portsWithTimezoneMismatch: [],
    duplicateNames: [],
    duplicateCoordinates: []
  };

  for (const result of validationResults) {
    if (result.isValid && result.errors.length === 0) {
      summary.valid++;
    }
    
    if (result.errors.length > 0) {
      summary.withErrors++;
      
      for (const error of result.errors) {
        const key = error.issue;
        summary.errorsByType[key] = (summary.errorsByType[key] || 0) + 1;
        
        // Collect specific problematic ports
        if (key === 'INVALID_LATITUDE' || key === 'INVALID_LONGITUDE' ||
            key === 'LATITUDE_OUT_OF_RANGE' || key === 'LONGITUDE_OUT_OF_RANGE') {
          summary.portsWithInvalidCoordinates.push(result);
        }
      }
    }
    
    if (result.warnings.length > 0) {
      summary.withWarnings++;
      
      for (const warning of result.warnings) {
        const key = warning.issue;
        summary.warningsByType[key] = (summary.warningsByType[key] || 0) + 1;
        
        // Collect specific problematic ports
        if (key === 'MISSING_LOCODE') {
          summary.portsMissingLocode.push(result);
        } else if (key === 'TIMEZONE_MISMATCH') {
          summary.portsWithTimezoneMismatch.push(result);
        } else if (key === 'DUPLICATE_NAME') {
          summary.duplicateNames.push(result);
        } else if (key === 'DUPLICATE_COORDINATES') {
          summary.duplicateCoordinates.push(result);
        }
      }
    }
  }

  return summary;
}
